import { watch } from 'vue'
import createLogger from './logger'
import { STORE_RELOAD_RATE_LIMIT_MESSAGE } from './storeReloadRateLimit'
import { markImpendingReload } from './reloadLoopMonitor'
import { reportStoreReloadRateLimitToSentry } from './sentryStoreReload'
import { tryConsumeAutoReload } from './autoReloadBudget'

const logger = createLogger('utils/storeUtils.ts')
const STORE_ERROR_RELOAD_KEY = 'store_error_reload_timestamps'
const STORE_ERROR_RELOAD_LIMIT = 2
const STORE_ERROR_RELOAD_WINDOW_MS = 30_000

export async function waitUntilReady(options: {
  continueOnTrue: () => boolean
  timeoutMs?: number
}) {
  const { continueOnTrue: checkStatusFn, timeoutMs } = {
    timeoutMs: 5000,
    ...options,
  }
  return new Promise<boolean>((resolve) => {
    let resolved = false
    let unwatch: (() => void) | null = null

    const cleanup = () => {
      if (unwatch) {
        unwatch()
        unwatch = null
      }
    }

    // Set up timeout
    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true
        cleanup()
        resolve(false) // Return false on timeout
      }
    }, timeoutMs)

    unwatch = watch(
      checkStatusFn,
      (newValue) => {
        if (newValue && !resolved) {
          resolved = true
          clearTimeout(timeout)
          cleanup()
          resolve(true) // Return true when loading is done
        }
      },
      { immediate: true },
    )
  })
}

export async function hardReloadOnStoreError() {
  const now = Date.now()
  const existingTimestamps = getRecentReloadTimestamps(now)

  if (existingTimestamps.length >= STORE_ERROR_RELOAD_LIMIT) {
    const error = new Error(STORE_RELOAD_RATE_LIMIT_MESSAGE)
    logger.warn(error.message, {
      reloadTimestamps: existingTimestamps,
      windowMs: STORE_ERROR_RELOAD_WINDOW_MS,
      limit: STORE_ERROR_RELOAD_LIMIT,
    })
    reportStoreReloadRateLimitToSentry(existingTimestamps)
    localStorage.removeItem(STORE_ERROR_RELOAD_KEY)
    throw error
  }

  const budget = tryConsumeAutoReload('store_hydration_hard_reload')
  if (!budget.ok) {
    const error = new Error(STORE_RELOAD_RATE_LIMIT_MESSAGE)
    logger.warn('Store hydration reload blocked by global auto-reload budget', {
      reloadTimestamps: existingTimestamps,
    })
    reportStoreReloadRateLimitToSentry(existingTimestamps)
    throw error
  }

  const nextTimestamps = [...existingTimestamps, now]
  localStorage.setItem(STORE_ERROR_RELOAD_KEY, JSON.stringify(nextTimestamps))

  markImpendingReload('store_hydration_hard_reload')
  window.location.href = '/'
}

function getRecentReloadTimestamps(now: number): number[] {
  try {
    const rawTimestamps = localStorage.getItem(STORE_ERROR_RELOAD_KEY)
    const parsedTimestamps = rawTimestamps ? JSON.parse(rawTimestamps) : []

    if (!Array.isArray(parsedTimestamps)) return []

    return parsedTimestamps.filter((value): value is number => {
      return (
        typeof value === 'number' &&
        Number.isFinite(value) &&
        now - value <= STORE_ERROR_RELOAD_WINDOW_MS
      )
    })
  } catch (error) {
    logger.error('Failed to parse store error reload timestamps', error)
    localStorage.removeItem(STORE_ERROR_RELOAD_KEY)
    return []
  }
}
